import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import {
    DreamTeamCrossFunctionId,
    DreamTeamFunctionType,
    TeamImprovementId
} from '../../constants-types';
import { Group, SearchResult, SearchResultSet } from '../../core-data-model';
import {
    ContextDataService,
    GroupsDataService,
    notification
} from '../../core-services';
import { TranslationService } from '../../translations';

@Component({
    selector: 'update-group',
    template: `
        <div class="mediumText" style="margin-bottom: 16px">
            {{ TR.key('teams.liketoadd') }}
        </div>
        <div style="margin-bottom: 24px">
            <add-to-group-pillbox
                [isProcessing]="submitting"
                [groupSet]="setOfPeople"
                (onAdded)="addResult($event)"
                (onRemove)="removeResult($event)"
            >
            </add-to-group-pillbox>
        </div>
        <div style="margin-bottom: 24px; width: 70%; min-width: 400px">
            <group-typeahead [(ngModel)]="group"> </group-typeahead>
        </div>
        @if (makingNewGroup) {
            <div style="position: relative; margin-bottom: 24px">
                <label
                    class="mediumText"
                    style="position: absolute; top: -24px;"
                >
                    @if (
                        !submitting && groupsDataService.nameExists(groupName)
                    ) {
                        <span class="errorColor" style="margin-left: 10px;">
                            {{ TR.key('groups.nameExists') }}
                        </span>
                    }
                </label>
                <input
                    [(ngModel)]="groupName"
                    [placeholder]="TR.key('label.myTeams.team-name')"
                    class="simpleInput"
                    style="width: 70%; min-width: 400px"
                    [disabled]="submitting"
                />
            </div>
        }
        @if (!makingNewGroup) {
            <div style="margin-bottom: 34px">
                <button
                    type="button"
                    class="btn btn--lsg"
                    (click)="goingToMakeNewGroup()"
                >
                    {{ TR.key('browseTeams.addNewTeam') }}
                </button>
            </div>
        }
        <div class="form" style="margin-bottom: 24px">
            <div class="form_buttonGroup form_mainControls">
                <button
                    [disabled]="!readyToSave()"
                    type="button"
                    [ngClass]="{ 'btn--lsg--loading': submitting }"
                    class="btn btn--lsg btn--lsg--primary"
                    (click)="saveGroup()"
                >
                    {{ TR.key('text.common-terms.save') }}
                </button>
            </div>
        </div>
    `,
    standalone: false
})
export class UpdateGroupComponent {
    @Input()
    setOfPeople: SearchResultSet;

    group: Group;
    makingNewGroup = false;
    reportForm: UntypedFormGroup;
    submitting = false;
    groupName = '';
    teamFunctionType: DreamTeamFunctionType = DreamTeamFunctionType.Unknown;
    otherTeamFunction: string;
    crossFunctionId: DreamTeamCrossFunctionId =
        DreamTeamCrossFunctionId.Unknown;
    teamImprovementIds: TeamImprovementId[] = [TeamImprovementId.Unknown];
    otherTeamImprovement: string;
    copyOfContext: SearchResult[];

    @Output()
    readonly modalClosed = new EventEmitter<void>();
    @Output()
    readonly selectionChanged = new EventEmitter<SearchResultSet>();

    constructor(
        public TR: TranslationService,
        public context: ContextDataService,
        public groupsDataService: GroupsDataService
    ) {}

    goingToMakeNewGroup() {
        this.makingNewGroup = true;
    }

    addResult(result: SearchResult) {
        this.selectionChanged.emit(this.setOfPeople.addToResultSet(result));
    }
    removeResult(result: SearchResult) {
        this.selectionChanged.emit(
            this.setOfPeople.removeFromResultSet(result)
        );
    }

    readyToSave() {
        const hasContent = this.setOfPeople.size > 0;
        if (this.makingNewGroup) {
            return (
                this.groupName &&
                !this.groupsDataService.nameExists(this.groupName) &&
                hasContent
            );
        } else if (this.group) {
            return hasContent;
        }
        return false;
    }

    @notification('groupsupdated.success-title', 'teamsUpdated.success-message')
    async saveGroup() {
        this.submitting = true;
        let myGroup: Group;
        let myGroupContents: SearchResult[] = [];
        let filteredSet: SearchResult[];
        try {
            if (this.makingNewGroup) {
                myGroup = await this.groupsDataService.createGroup(
                    this.groupName,
                    this.teamFunctionType,
                    this.otherTeamFunction,
                    this.crossFunctionId,
                    this.teamImprovementIds,
                    this.otherTeamImprovement
                );
            } else {
                myGroup = this.group;
                myGroupContents =
                    await this.groupsDataService.getGroupContentsByGroup(
                        myGroup
                    );
            }
            // This could be made more performanent with hashtables if needed
            filteredSet = this.setOfPeople
                .filter(
                    (entryToAdd): entryToAdd is SearchResult =>
                        !myGroupContents.find(
                            (existingEntry) =>
                                entryToAdd.id === existingEntry.id
                        )
                )
                .toArray();
            if (filteredSet.length > 0) {
                await this.groupsDataService.addEntitiesToGroup(
                    myGroup,
                    filteredSet
                );
            }
            if (this.makingNewGroup) {
                this.makingNewGroup = false;
            }
            this.context.selectedGroup = myGroup;
            this.context.tableContextUpdate.emit();
            this.modalClosed.emit();
        } finally {
            this.submitting = false;
        }
    }
}
